import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Container, Typography, Avatar, Paper, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import axios from 'axios';
import { API_URL } from '../utils/api';
import PartnerRequestButton from '../components/PartnerRequestButton';

const ProfilePaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: 24,
  background: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(10px)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease-in-out',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 12px 40px rgba(255, 105, 180, 0.15)',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '5px',
    background: 'linear-gradient(45deg, #FF69B4, #8E37D7)',
  }
}));

const LargeAvatar = styled(Avatar)(({ theme }) => ({
  width: 140,
  height: 140,
  border: '4px solid #fff',
  boxShadow: '0 8px 32px rgba(107, 141, 214, 0.2)',
  backgroundColor: theme.palette.primary.light,
  margin: '0 auto',
  transition: 'transform 0.3s ease-in-out',
  '&:hover': {
    transform: 'scale(1.05)',
  }
}));

const ProfileName = styled(Typography)(({ theme }) => ({
  fontSize: '2rem',
  fontWeight: 600,
  textAlign: 'center',
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(3),
  background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
}));

const InfoItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  marginBottom: theme.spacing(2),
  '& svg': {
    marginRight: theme.spacing(2),
    color: theme.palette.primary.main,
    fontSize: '1.5rem',
  }
}));

const LocationText = styled(Typography)(({ theme }) => ({
  '&.location-details': {
    color: theme.palette.text.secondary,
    fontSize: '0.9rem',
    marginTop: theme.spacing(0.5),
    paddingLeft: theme.spacing(4),
  }
}));

const UserProfile = () => {
  const { userId } = useParams();
  const [userData, setUserData] = useState(null);
  const [partnerData, setPartnerData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUserData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/users/${userId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setUserData(response.data);
      
      // If user has a partner and it's populated
      if (response.data.partner && typeof response.data.partner === 'object') {
        setPartnerData(response.data.partner);
      }
      // If partner is just an ID, fetch partner data
      else if (response.data.partner) {
        const partnerResponse = await axios.get(`${API_URL}/api/users/${response.data.partner}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        setPartnerData(partnerResponse.data);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserData();
  }, [userId]);

  const handlePartnerRequestSent = () => {
    // Refresh user data after sending partner request
    fetchUserData();
  };

  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  if (!userData) {
    return <Typography>User not found</Typography>;
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <ProfilePaper elevation={3}>
        <Box sx={{ position: 'relative', textAlign: 'center', mb: 3 }}>
          <LargeAvatar
            src={userData.avatar}
            alt={`${userData.firstName} ${userData.lastName}`}
          >
            {userData.firstName?.charAt(0)}
          </LargeAvatar>
        </Box>

        <ProfileName variant="h2">
          {`${userData.firstName} ${userData.lastName}`}
        </ProfileName>

        <Box>
          <InfoItem>
            <LocationOnIcon sx={{ color: '#FF69B4' }} />
            <Box>
              <LocationText>
                {userData.location?.coordinates ? 
                  `${userData.location.coordinates[1].toFixed(6)}, ${userData.location.coordinates[0].toFixed(6)}` :
                  'No location set'}
              </LocationText>
            </Box>
          </InfoItem>
          <InfoItem>
            <CalendarTodayIcon sx={{ color: '#FF69B4' }} />
            <Typography>
              {userData.birthday ? new Date(userData.birthday).toLocaleDateString() : 'No birthday set'}
            </Typography>
          </InfoItem>
          <Typography
            variant="body1"
            sx={{
              mt: 2,
              fontStyle: 'italic',
              textAlign: 'center',
              color: 'text.secondary',
              padding: 2,
              borderLeft: '3px solid',
              borderColor: '#FF69B4',
              backgroundColor: 'rgba(255, 105, 180, 0.05)',
              borderRadius: '0 8px 8px 0',
            }}
          >
            {userData.bio || 'No bio available'}
          </Typography>

          <Box sx={{ mt: 4, textAlign: 'center' }}>
            {partnerData ? (
              <Box>
                <Typography variant="h6" gutterBottom>Partner</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                  <Avatar
                    src={partnerData.avatar}
                    alt={`${partnerData.firstName} ${partnerData.lastName}`}
                    sx={{ width: 64, height: 64, mr: 2 }}
                  />
                  <Typography>
                    {`${partnerData.firstName} ${partnerData.lastName}`}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <PartnerRequestButton
                userId={userId}
                onRequestSent={handlePartnerRequestSent}
              />
            )}
          </Box>
        </Box>
      </ProfilePaper>
    </Container>
  );
};

export default UserProfile; 